// One advisor: a coloured face, a doctrine line, the play it is proposing right now, what the play would
// cost on the command net, and the evidence it used. Spec §6 — the card is a proposal, never an outcome,
// so everything that would let a player distrust it is on the face of it: the age of the play, whether it
// came from the model or a template, whether its citations checked out, and every order that was dropped
// for being illegal, with the reason.

import { useState, type ReactNode } from 'react';
import { personaLabel, type AdvisorPlayState, type AdvisorRelation } from '../../ai/advisors';
import type { AdvisorPersona } from '../../ai/prompts';
import type { Stance } from '../../ai/schemas';
import type { ValidOrder } from '../../ai/validate';
import { Acronym } from '../Acronym';
import { clockHM } from '../reports';
import { Chip, fmtDur } from './ui';
import { PlayCard } from './PlayCard';

/**
 * Which staff officer each advisor is, in the abbreviation a planner would use. It hangs beside the
 * persona name as an `<Acronym>`, so "S-3" says "the operations officer" on hover rather than being a
 * piece of decoration.
 */
const STAFF_SECTION: Record<string, string | undefined> = {
  maneuver: 's-3',
  fires: 'fso',
  recon: 's-2',
};

/** One order of the play, with everything the row needs already resolved. */
export interface PlayRow { order: ValidOrder; callsign: string; targetSummary: string }

/** A cited event: the chip pings this cell on the map while the pointer is on it. */
export interface Citation { id: number; cell?: number }

export function AdvisorCard({
  persona, state, refreshing, streaming, relation, expanded, ageMin, ageTick, cost, citations, rows,
  actLabel, actTitle, retargetIdx, retargetReason,
  onExecute, onExpand, onRemove, onRetarget, onExecuteEdited, onPing,
}: {
  persona: AdvisorPersona;
  state: AdvisorPlayState | null;
  refreshing: boolean;
  /**
   * The rationale of the answer being written right now, as far as it has arrived, or null when
   * nothing is in flight. While it is a string the card shows the previous play dimmed under a
   * "previous" chip and this text in its place - a refresh you can read, rather than a spinner.
   */
  streaming: string | null;
  /** How this advisor stands to the other two: a declared disagreement, or an inferred concurrence. */
  relation: AdvisorRelation;
  expanded: boolean;
  /** Sim minutes since the play was built. */
  ageMin: number;
  /** The tick the play was built at, for the age badge's own H+HH:MM stamp. */
  ageTick?: number;
  cost: { issueSec: number; txSec: number };
  citations: Citation[];
  rows: PlayRow[];
  /** "execute play" in the live stage, "adopt into plan" before H-hour. */
  actLabel: string;
  actTitle: string;
  /** Index of the row currently out on the map for a new target, if any. */
  retargetIdx: number | null;
  retargetReason?: string;
  onExecute: () => void;
  onExpand: () => void;
  onRemove: (idx: number) => void;
  onRetarget: (idx: number) => void;
  onExecuteEdited: () => void;
  onPing: (cell: number | null) => void;
}) {
  const stale = ageMin > 3;
  const canAct = rows.length > 0;
  // How many of the model's own orders did not survive validation. The cost line below already sums
  // only the survivors (`rows`); the rationale claimed the whole play, which is how a card came to
  // read "mass the battalion's fires on the ridge" over a single surviving smoke mission.
  const dropped = state?.validated.rejected.length ?? 0;
  const proposed = dropped + (state?.validated.orders.length ?? 0);
  // The rationale is clamped to two lines so three cards always fit the rail without scrolling
  // (spec §8 tone; no dropdowns and no scroll-to-find). Hover/focus reveals the rest via CSS alone;
  // a click pins it open for touch, where there is no hover.
  const [rationaleOpen, setRationaleOpen] = useState(false);
  // A new answer is on its way and has started arriving. `streaming` is only ever non-null between the
  // first characters of a model answer and the validated play that replaces it.
  const drafting = streaming !== null && streaming.length > 0;

  return (
    <article
      data-testid={`advisor-${persona.id}`}
      className="wt-card fade-in"
      style={{ borderColor: expanded ? persona.color : 'var(--color-slate-700)' }}
    >
      <header className="flex items-start gap-2">
        <Face persona={persona} />
        <div className="min-w-0 flex-1">
          <div className="mono flex items-baseline gap-1.5 truncate text-[11px] tracking-[0.14em]" style={{ color: persona.color }}>
            <span className="truncate">{persona.name}</span>
            {STAFF_SECTION[persona.id] && (
              <span className="text-[9px] tracking-[0.1em] text-fog">
                <Acronym k={STAFF_SECTION[persona.id]!} />
              </span>
            )}
          </div>
          <div className="truncate text-[9px] text-fog">{persona.doctrine}</div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          {refreshing && <Chip testId={`refreshing-${persona.id}`} title="Asking the model for a new play">refreshing</Chip>}
          {state && (
            <Chip
              tone={stale ? 'warn' : 'neutral'}
              active={stale}
              testId={`age-${persona.id}`}
              title={`Built at ${ageTick === undefined ? 'H-hour' : clockHM(ageTick)} — ${Math.floor(ageMin)} sim minutes ago`}
            >
              {ageLabel(ageMin, ageTick)}
            </Chip>
          )}
        </div>
      </header>

      {!state ? (
        <p className="mono mt-1.5 text-[10px] text-fog">standing by</p>
      ) : (
        <>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span
              data-testid={`play-name-${persona.id}`}
              className="min-w-0 flex-1 truncate text-[12px] font-medium text-paper"
              // Dimmed while a new answer is being written: this is last minute's advice, still
              // standing and still executable, but no longer the freshest thing on the card.
              style={drafting ? { opacity: 0.55 } : undefined}
            >
              {state.play.name}
            </span>
            {drafting && (
              <Chip testId={`previous-${persona.id}`} title="The play above is the previous one; a new answer is being written below">
                previous
              </Chip>
            )}
            {!drafting && state.play.stance && state.play.stance !== 'continue' && (
              <Chip
                tone={state.play.stance === 'urgent' ? 'warn' : 'neutral'}
                active={state.play.stance === 'urgent'}
                testId={`stance-${persona.id}`}
                title={STANCE_TITLE[state.play.stance]}
              >
                {state.play.stance}
              </Chip>
            )}
            {state.source === 'template' && (
              <Chip testId={`source-${persona.id}`} title={state.error ?? state.note ?? 'Built from a template, not the model'}>template</Chip>
            )}
            {state.flagged && (
              <Chip tone="warn" active testId={`flagged-${persona.id}`} title="Most of the cited events do not exist; treat this play with suspicion">
                flagged
              </Chip>
            )}
          </div>

          {/* The argument between the three advisors, on the face of the card: a declared conflict in
              amber on both cards involved, or an inferred agreement in fog. Two advisors who converge
              look like a duplicate rendering unless the interface says they converged on purpose. */}
          {(relation.disagreesWith || relation.concursWith) && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {relation.disagreesWith && (
                <Chip
                  tone="warn"
                  active
                  testId={`disagrees-${persona.id}`}
                  title={`${personaLabel(relation.disagreesWith.from)}: “${relation.disagreesWith.reason}”`}
                >
                  disagrees with {personaLabel(relation.disagreesWith.persona)}
                </Chip>
              )}
              {relation.concursWith && (
                <Chip
                  testId={`concurs-${persona.id}`}
                  title={`These two plays agree — ${relation.concursWith.because}`}
                >
                  concurs with {personaLabel(relation.concursWith.persona)}
                </Chip>
              )}
            </div>
          )}

          <p
            data-testid={`rationale-${persona.id}`}
            data-streaming={drafting ? 'true' : 'false'}
            className={`mt-0.5 cursor-pointer text-[10px] leading-snug hover:text-paper focus:text-paper focus:outline-none ${
              drafting ? 'text-paper' : 'text-fog'
            } ${rationaleOpen || drafting ? '' : 'line-clamp-2 hover:line-clamp-none focus:line-clamp-none'}`}
            title={drafting ? 'The model is writing this answer now' : state.play.rationale}
            tabIndex={0}
            role="button"
            aria-expanded={rationaleOpen}
            aria-busy={drafting}
            onClick={() => setRationaleOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              setRationaleOpen((v) => !v);
            }}
          >
            {drafting ? (
              <>
                {streaming}
                <span className="caret-blink" aria-hidden>▍</span>
              </>
            ) : (
              <>
                {state.play.rationale}
                {dropped > 0 && (
                  <span className="text-warn"> ({dropped} of {proposed} orders dropped)</span>
                )}
              </>
            )}
          </p>

          <div className="mt-1 flex flex-wrap items-center gap-1">
            {citations.map((c) => (
              <span
                key={c.id}
                onPointerEnter={() => onPing(c.cell ?? null)}
                onPointerLeave={() => onPing(null)}
              >
                <Chip testId={`cite-${persona.id}-${c.id}`} title="The event this play is built on — hover to ping the map">
                  #{c.id}
                </Chip>
              </span>
            ))}
            <span
              className="mono ml-auto text-[9px] text-fog"
              title={
                'Total command-net issue time, and the longest transmission of the bundle'
                + (dropped > 0 ? ' — for the orders that survived validation, not the ones that were dropped' : '')
              }
            >
              Σ {fmtDur(cost.issueSec)} · tx ≤ {fmtDur(cost.txSec)}
            </span>
          </div>

          {state.validated.rejected.length > 0 && (
            <ul className="mt-1 space-y-0.5" data-testid={`dropped-${persona.id}`}>
              {state.validated.rejected.map((r, i) => (
                <li key={i} className="mono truncate text-[9px] text-warn" title={r.reason}>
                  dropped: {r.reason}
                </li>
              ))}
            </ul>
          )}

          {/* The TEMPLATE chip above already carries this exact text as its hover title (spec §8: no
              information duplicated as a second always-on line - hover/title is the one disclosure
              pattern, same as the rationale). Dropping the standalone line is also what buys three
              cards the vertical room to fit the rail without scrolling at 1440x900. */}

          <div className="mt-1 flex items-center gap-1">
            <Action testId={`execute-${persona.id}`} color={persona.color} disabled={!canAct} onClick={onExecute} title={actTitle}>
              {actLabel}
            </Action>
            <Action testId={`expand-${persona.id}`} color="var(--color-slate-700)" onClick={onExpand} title="Open the order cards to edit them first">
              {expanded ? 'collapse' : 'expand'}
            </Action>
          </div>

          {expanded && (
            <div className="mt-1.5 border-t border-slate-700 pt-1.5">
              {rows.length === 0 ? (
                <p className="mono text-[10px] text-fog">no cards left in this play</p>
              ) : (
                <ul className="space-y-1">
                  {rows.map((row, i) => (
                    <PlayCard
                      key={`${row.order.unitId}-${row.order.card}-${i}`}
                      order={row.order}
                      callsign={row.callsign}
                      targetSummary={row.targetSummary}
                      testIdPrefix={`order-${persona.id}-${i}`}
                      retargeting={retargetIdx === i}
                      retargetReason={retargetReason}
                      onRemove={() => onRemove(i)}
                      onRetarget={() => onRetarget(i)}
                    />
                  ))}
                </ul>
              )}
              <div className="mt-1.5">
                <Action
                  testId={`execute-edited-${persona.id}`}
                  color={persona.color}
                  disabled={rows.length === 0}
                  onClick={onExecuteEdited}
                  title={actTitle}
                >
                  {actLabel} (edited)
                </Action>
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
}

/** What each stance means, for the chip's hover text. */
const STANCE_TITLE: Record<Stance, string> = {
  continue: 'The same play as last refresh: this advisor is standing by what it already said',
  change: 'A different play from last refresh: the situation moved and this advisor changed its mind',
  urgent: 'Act this refresh — this advisor says waiting for the next one loses the chance',
};

/** The initial letters in the persona's colour: a face, without pretending to be a person. */
function Face({ persona }: { persona: AdvisorPersona }) {
  return (
    <span
      className="mono flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[10px] tracking-[0.05em]"
      style={{
        borderColor: persona.color,
        color: persona.color,
        background: `color-mix(in srgb, ${persona.color} 14%, transparent)`,
      }}
      aria-hidden
    >
      {persona.initials}
    </span>
  );
}

function Action({
  children, onClick, color, disabled = false, testId, title,
}: { children: ReactNode; onClick: () => void; color: string; disabled?: boolean; testId: string; title: string }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      title={title}
      className="mono flex-1 rounded-sm border px-1.5 py-1 text-[10px] uppercase tracking-[0.14em]"
      style={{
        borderColor: disabled ? 'var(--color-slate-700)' : color,
        color: disabled ? 'var(--color-fog)' : 'var(--color-paper)',
        background: disabled ? 'transparent' : `color-mix(in srgb, ${color} 16%, transparent)`,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * The age badge, on the one clock: when the play was built, not how long ago in its own private units.
 *
 * "44 MIN AGO" beside a top bar reading H+03:52 was the third of the review's three time formats. The
 * badge now stamps `H+HH:MM` like everything else, and the elapsed minutes are in its tooltip.
 */
function ageLabel(ageMin: number, ageTick?: number): string {
  if (ageTick === undefined) return Math.floor(ageMin) < 1 ? 'now' : `${Math.floor(ageMin)} min`;
  return clockHM(ageTick);
}
